import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaCheckCircle,
    FaCoins,
    FaCreditCard,
    FaGlobe,
    FaHeadset,
    FaPoundSign,
    FaReceipt,
    FaShieldAlt,
    FaShoppingCart,
    FaTruck,
    FaUndo
} from "react-icons/fa";
import {
    TiArrowRight,
    TiArrowUp
} from "react-icons/ti";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const SalesPolicy = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: section,
              start: "top 90%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const salesPrinciples = [
    {
      id: "SP-01",
      title: "Pricing Framework",
      subtitle: "Transparent Value Protocol",
      desc: "All prices are displayed in GBP and inclusive of VAT unless stated otherwise. Token prices are subject to market volatility and quoted at the time of transaction.",
      icon: <FaPoundSign />,
      highlights: ["GBP Currency", "VAT Inclusive", "Real-Time Pricing"]
    },
    {
      id: "SP-02",
      title: "Payment Methods",
      subtitle: "Secure Transaction Protocol",
      desc: "We accept major credit cards, bank transfers, and approved cryptocurrencies. All payments are processed through institutional-grade secure gateways.",
      icon: <FaCreditCard />,
      highlights: ["Credit Cards", "Bank Transfer", "Crypto Accepted"]
    },
    {
      id: "SP-03",
      title: "Order Confirmation",
      subtitle: "Transaction Verification",
      desc: "Upon successful payment, you will receive an instant email confirmation with your transaction ID. Blockchain transactions are verified within 15 minutes.",
      icon: <FaReceipt />,
      highlights: ["Instant Confirmation", "Transaction ID", "15-Min Verification"]
    },
    {
      id: "SP-04",
      title: "Refund Protocol",
      subtitle: "Consumer Protection",
      desc: "Eligible refunds are processed within 14 days of purchase. Digital assets and token purchases may have specific refund limitations disclosed at checkout.",
      icon: <FaUndo />,
      highlights: ["14-Day Window", "Consumer Rights", "Fair Processing"]
    },
    {
      id: "SP-05",
      title: "Digital Delivery",
      subtitle: "Instant Fulfilment",
      desc: "Digital products and tokens are delivered immediately after payment confirmation. Access credentials are sent directly to your registered email.",
      icon: <FaTruck />,
      highlights: ["Instant Delivery", "Email Credentials", "Immediate Access"]
    },
    {
      id: "SP-06",
      title: "Dispute Resolution",
      subtitle: "Support Protocol",
      desc: "Our dedicated support team handles all transaction disputes within 48 hours. Contact us immediately for any order issues or concerns.",
      icon: <FaHeadset />,
      highlights: ["48-Hour Response", "Dedicated Support", "Fair Resolution"]
    }
  ];

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50 font-sans">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/sales-policy-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="salesHeroSparkles"
              background="transparent"
              minSize={0.6}
              maxSize={1.4}
              particleDensity={50}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Transaction Protocol"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Institutional Commerce & Sales Framework
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-32 mx-auto max-w-7xl">
        
        {/* Transaction Header */}
        <div className="mb-24 p-12 rounded-[4rem] bg-gradient-to-r from-amber-500/10 to-transparent border border-white/10">
           <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
              <div>
                 <div className="text-[10px] font-bold text-amber-500 uppercase tracking-[0.4em] mb-4">Commerce Framework</div>
                 <h2 className="text-3xl md:text-4xl font-bold text-white tracking-widest leading-tight">Sales & Transaction Policy</h2>
              </div>
              <div className="flex flex-col gap-2 text-right">
                 <div className="text-[9px] font-bold text-gray-600 uppercase tracking-widest">Currency</div>
                 <div className="text-sm font-bold text-white">GBP (£)</div>
                 <div className="text-[9px] font-bold text-gray-600 uppercase tracking-widest mt-2">VAT Status</div>
                 <div className="text-sm font-bold text-amber-400">Inclusive (20%)</div>
              </div>
           </div>
        </div>

        {/* Sales Principles Bento */}
        <div className="mb-48">
           <div className="mb-16 flex flex-col md:flex-row justify-between items-end gap-8">
              <div className="max-w-xl">
                 <div className="text-[10px] font-bold text-orange-500 uppercase tracking-[0.4em] mb-4">Transaction Directives</div>
                 <h2 className="text-4xl md:text-5xl font-bold text-white tracking-widest leading-tight">Sales Principles</h2>
              </div>
              <p className="text-gray-300 italic text-sm max-w-xs md:text-right">
                 Transparent, secure, and consumer-first commerce across the Zero-Ecosystem.
              </p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {salesPrinciples.map((principle, index) => (
                <motion.div
                  key={principle.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative group rounded-[3rem] bg-white/5 border border-white/10 hover:border-amber-500/30 transition-all hover:bg-white/10 overflow-hidden flex flex-col p-10"
                  ref={(el) => (sectionRefs.current[index] = el)}
                >
                  <div className="absolute top-8 right-10 text-4xl text-amber-500/20 group-hover:scale-110 transition-transform">
                     {principle.icon}
                  </div>
                  <div className="text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-2">{principle.id}</div>
                  <h3 className="text-xl font-bold text-white mb-2 tracking-widest">{principle.title}</h3>
                  <div className="text-[10px] font-bold text-amber-400 uppercase tracking-[0.2em] mb-6">{principle.subtitle}</div>
                  <p className="text-gray-300 text-xs leading-relaxed mb-8 flex-grow">{principle.desc}</p>
                  
                  <div className="flex flex-wrap gap-2 mt-auto">
                     {principle.highlights.map((h, i) => (
                       <span key={i} className="text-[8px] font-bold text-gray-600 border border-white/5 px-3 py-1 rounded-full uppercase tracking-widest group-hover:border-amber-500/20 group-hover:text-gray-300 transition-colors">
                          {h}
                       </span>
                     ))}
                  </div>
                </motion.div>
              ))}
           </div>
        </div>

        {/* Transaction Status Terminal */}
        <div className="mb-48">
           <div className="p-2 rounded-[4rem] bg-gradient-to-br from-amber-500/20 to-transparent border border-white/10 overflow-hidden shadow-2xl">
              <div className="bg-black/80 backdrop-blur-3xl p-16 relative">
                 <div className="flex flex-col lg:flex-row gap-16 items-center">
                    <div className="flex-1">
                       <div className="flex items-center gap-4 mb-8">
                          <div className="w-16 h-16 rounded-2xl bg-amber-500/20 flex items-center justify-center text-amber-400 text-3xl">
                             <FaShoppingCart />
                          </div>
                          <div>
                             <div className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.4em]">Commerce Engine</div>
                             <h3 className="text-2xl font-bold text-white tracking-widest">Transaction Status</h3>
                          </div>
                       </div>
                       <div className="grid grid-cols-2 gap-6">
                          {[
                            { l: "Payment Gateway", v: "Institutional Secure" },
                            { l: "Currency Support", v: "GBP / ETH / ZERO" },
                            { l: "Refund Window", v: "14 Days" },
                            { l: "Verification Time", v: "< 15 Minutes" }
                          ].map((item, i) => (
                            <div key={i} className="flex flex-col gap-1">
                               <div className="text-[8px] font-bold text-gray-600 uppercase tracking-widest">{item.l}</div>
                               <div className="text-sm font-bold text-white italic tracking-wider">{item.v}</div>
                            </div>
                          ))}
                       </div>
                    </div>

                    <div className="flex-shrink-0 flex flex-col items-center gap-6">
                       <div className="w-32 h-32 rounded-[2rem] bg-emerald-600 border-2 border-emerald-400 flex items-center justify-center text-5xl text-white shadow-[0_0_40px_rgba(16,185,129,0.4)]">
                          <FaCheckCircle />
                       </div>
                       <div className="text-center">
                          <div className="text-sm font-bold text-emerald-400 uppercase tracking-widest">Gateway Active</div>
                          <div className="text-[9px] text-gray-600 mt-2">Secure Transactions</div>
                       </div>
                    </div>
                 </div>

                 <div className="mt-16 pt-8 border-t border-white/5">
                    <div className="w-full h-2 rounded-full bg-white/5 overflow-hidden">
                       <motion.div 
                        initial={{ width: 0 }}
                        whileInView={{ width: "100%" }}
                        transition={{ duration: 2.5, ease: "easeOut" }}
                        className="h-full bg-gradient-to-r from-amber-600 to-orange-600 shadow-[0_0_15px_rgba(245,158,11,0.5)]"
                       />
                    </div>
                    <div className="mt-4 flex justify-between text-[8px] font-bold text-gray-600 uppercase tracking-widest">
                       <span>Commerce Engine Active</span>
                       <span>100%</span>
                    </div>
                 </div>
              </div>
           </div>
        </div>

        {/* Final CTA */}
        <motion.div
           initial={{ opacity: 0, scale: 0.95 }}
           whileInView={{ opacity: 1, scale: 1 }}
           viewport={{ once: true }}
           className="relative text-center py-48"
        >
           <div className="absolute inset-0 bg-amber-500/5 rounded-[100%] blur-[150px]" />
           <div className="relative z-10 max-w-4xl mx-auto px-6">
              <div className="w-24 h-24 rounded-[3rem] bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-5xl text-amber-400 mx-auto mb-16 shadow-[0_0_50px_rgba(245,158,11,0.2)]">
                <FaCoins />
              </div>
              <h2 className="text-5xl md:text-7xl font-bold mb-12 leading-tight">
                 Initialize <span className="font-zentry italic text-amber-400 text-8xl uppercase tracking-tighter block mt-4">Transaction</span> Protocol
              </h2>
              <p className="text-gray-300 text-xl leading-relaxed mb-20 max-w-2xl mx-auto italic">
                Secure, transparent, and instant commerce across the entire Zero-Ecosystem.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
                 <GlassButton
                   variant="amber"
                   size="lg"
                   className="px-16 py-8 text-xl"
                   onClick={() => {}}
                 >
                   Download Policy (.PDF)
                 </GlassButton>
                 
                 <GlassButton
                   variant="gray"
                   size="lg"
                   className="px-16 py-8 text-xl"
                   onClick={() => {}}
                 >
                   <div className="flex items-center gap-4 group">
                     Contact Sales Hub <TiArrowRight className="text-2xl text-amber-400 group-hover:translate-x-2 transition-transform" />
                   </div>
                 </GlassButton>
              </div>

              <div className="mt-32 flex flex-wrap justify-center gap-16 opacity-30 grayscale hover:grayscale-0 transition-all duration-700">
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaGlobe /> UK COMMERCE</div>
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaShieldAlt /> SECURE GATEWAY</div>
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaCoins /> CRYPTO ACCEPTED</div>
              </div>
           </div>
        </motion.div>

      </div>

      {/* Back to Top */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-amber-500/30 rounded-full shadow-lg shadow-amber-500/10 cursor-pointer hover:bg-black/60 hover:border-amber-400/50 transition-all duration-500 z-50 group"
          onClick={scrollToTop}
        >
          <TiArrowUp className="text-amber-400 group-hover:-translate-y-1 transition-transform duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider">BACK TO TOP</span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SalesPolicy;
